import {
  SEU_BORDER_STREAM_OFFSET,
  SEU_CANVAS_GRID_CELL_SIZE,
  SEU_INPUT_STREAM_OFFSET_BOTTOM,
  SEU_MIN_LENGTH_FOR_ARROW,
  SEU_STREAM_HANDLE_SIZE,
  SeuCanvasStreamPositionItem,
} from '@seu-types';

type SeuStreamPathArrowDir = 'up' | 'down' | 'left' | 'right';

type SeuStreamPathArrow = {
  dir: SeuStreamPathArrowDir;
  x: number;
  y: number;
};
export type SeuStreamPathStep = {
  dx: number;
  dy: number;
};

export function getOffsetForStreamHandle(
  numberOfStreamHandles: number,
  streamHandleIndex: number
): number {
  // Handles are rendered centered (flex-col justify-center) inside the component card,
  // which itself is centered (flex items-center justify-center) in the 288×288 grid cell.
  // The offset from the grid cell bottom edge for handle i of N equals:
  //   cellCenter + distanceFromCenter
  // where distanceFromCenter = ((N-1)/2 - i) * handleSize
  // For N=1, i=0 this reduces to cellCenter (144px), matching the old single-handle case.
  const distanceFromCenter =
    ((numberOfStreamHandles - 1) / 2 - streamHandleIndex) *
    SEU_STREAM_HANDLE_SIZE;

  return SEU_CANVAS_GRID_CELL_SIZE / 2 + distanceFromCenter;
}

export function convertStreamToPathStepsAndArrows(
  stream: SeuCanvasStreamPositionItem,
  startXPixel: number,
  startYPixel: number,
  numberOfStreamHandlesStart: number,
  numberOfStreamHandlesEnd: number,
  outputHandleIndex: number,
  inputHandleIndex: number,
  streamHandleOffsetStartX: number,
  streamHandleOffsetEndX: number
): { steps: SeuStreamPathStep[]; arrows: SeuStreamPathArrow[] } {
  const steps: SeuStreamPathStep[] = [];
  const arrows: SeuStreamPathArrow[] = [];

  const deltaY = stream.endY - stream.startY;
  const deltaYPixel = deltaY * SEU_CANVAS_GRID_CELL_SIZE;

  const deltaX = stream.endX - stream.startX;
  const deltaXPixel = deltaX * SEU_CANVAS_GRID_CELL_SIZE;

  const cummulatedPath = {
    x: startXPixel,
    y: startYPixel,
  };

  const offsetForStreamHandleStart = getOffsetForStreamHandle(
    numberOfStreamHandlesStart,
    outputHandleIndex
  );

  const offsetForStreamHandleEnd = getOffsetForStreamHandle(
    numberOfStreamHandlesEnd,
    inputHandleIndex
  );

  // The actual Y offset that was applied to startYPixel by the caller:
  // - regular streams (outputFromComponentId set):  offsetForStreamHandleStart
  // - input streams   (no outputFromComponentId):   SEU_INPUT_STREAM_OFFSET_BOTTOM
  // Using the wrong value here causes a mismatch in the 3-segment (deltaX === 1) edge case.
  const startYOffset = stream.outputFromComponentId
    ? offsetForStreamHandleStart
    : SEU_INPUT_STREAM_OFFSET_BOTTOM;

  // The exact SVG Y coordinate the path must reach (centre of the target input handle).
  // Total required Y displacement = -deltaYPixel + (startYOffset - offsetEnd).
  const endYPixel =
    startYPixel - deltaYPixel + startYOffset - offsetForStreamHandleEnd;

  // NOTES
  // deltaYPixel is negative when the stream goes up
  // deltaXPixel is negative when the stream goes left

  // If Stream goes straight left or right
  // Only applies when both handles are at the same Y (offsets are equal), adjacent cells, same row.
  // Later also check if input or output component is inverted
  if (
    offsetForStreamHandleStart === offsetForStreamHandleEnd &&
    deltaX === 1 &&
    deltaY === 0 &&
    stream.outputFromComponentId
  ) {
    steps.push({
      dx:
        deltaX < 0
          ? -(streamHandleOffsetStartX + streamHandleOffsetEndX)
          : streamHandleOffsetStartX + streamHandleOffsetEndX,
      dy: 0,
    });

    arrows.push({
      // if input and output is swapped, point arrow to left
      dir: 'right',
      x:
        cummulatedPath.x +
        (deltaX < 0 ? -streamHandleOffsetStartX : streamHandleOffsetStartX),
      y: cummulatedPath.y,
    });

    return { steps, arrows };
  }

  // Starting segment
  let dxStart = streamHandleOffsetStartX;

  if (deltaY < 0) {
    // Input is below output
    dxStart -= SEU_BORDER_STREAM_OFFSET;
  } else if (deltaY === 0) {
    // Input and output are on the same level
    if (deltaX < 0) {
      // Input is behind output
      dxStart -= SEU_BORDER_STREAM_OFFSET;
    } else if (deltaX > 0) {
      // Input is in front of output
      dxStart += SEU_BORDER_STREAM_OFFSET;
    }
  } else {
    // Input is above output
    dxStart += SEU_BORDER_STREAM_OFFSET;
  }

  steps.push({
    dx: dxStart,
    dy: 0,
  });

  cummulatedPath.x += dxStart;

  // Main vertical segment
  let dyMain = -deltaYPixel;

  if (deltaX === 1) {
    // ** EDGE CASE ** 3-SEGMENT STREAM
    // Move directly to the target handle Y in one step — no routing detour needed.
    dyMain = endYPixel - startYPixel;
  } else if (deltaY < 0) {
    // Input is below output
    if (deltaX <= 0) {
      // Input is behind or next to output
      dyMain -= SEU_BORDER_STREAM_OFFSET + SEU_CANVAS_GRID_CELL_SIZE;
    } else if (deltaX > 0) {
      // Input is in front of output
      dyMain -= SEU_CANVAS_GRID_CELL_SIZE - SEU_BORDER_STREAM_OFFSET;
    }
  } else if (deltaY === 0) {
    // Input and output are on the same level
    if (deltaX > 0) {
      // Input is in front of output
      dyMain -= SEU_CANVAS_GRID_CELL_SIZE - SEU_BORDER_STREAM_OFFSET;
    } else {
      // Input is behind output
      if (deltaX < 0) {
        dyMain -= SEU_BORDER_STREAM_OFFSET;
      } else {
        dyMain += SEU_BORDER_STREAM_OFFSET;
      }
    }
  } else if (deltaY > 0) {
    // Input is above output
    if (deltaX > 0) {
      // Input is in front of output
      dyMain += SEU_BORDER_STREAM_OFFSET;
    } else if (deltaX <= 0) {
      // Input is behind or directly above output
      dyMain -= SEU_BORDER_STREAM_OFFSET;
    }
  }

  // Compensate offset (skip for deltaX===1 as dyMain is already exact)
  if (deltaX !== 1) {
    if (!stream.outputFromComponentId) {
      dyMain += SEU_INPUT_STREAM_OFFSET_BOTTOM;
    } else {
      dyMain += offsetForStreamHandleStart;
    }
  }

  if (dyMain !== 0) {
    steps.push({
      dx: 0,
      dy: dyMain,
    });

    // Add one arrow for every 288 pixels of dyMain, but only if the segment is long enough to fit an arrow (considering border offsets)
    const numberOfArrowsDy =
      Math.abs(dyMain) >= SEU_MIN_LENGTH_FOR_ARROW
        ? Math.floor(Math.abs(deltaYPixel) / SEU_CANVAS_GRID_CELL_SIZE) || 1
        : 0;
    for (let i = 0; i < numberOfArrowsDy; i++) {
      const fullGridCellsUntilThisCell = Math.floor(
        cummulatedPath.y / SEU_CANVAS_GRID_CELL_SIZE
      );

      let firstArrowPosition =
        fullGridCellsUntilThisCell * SEU_CANVAS_GRID_CELL_SIZE;

      if (deltaY < 0) {
        firstArrowPosition += 0.9 * SEU_CANVAS_GRID_CELL_SIZE;
      } else if (deltaY === 0) {
        if (deltaX < 0) {
          firstArrowPosition += 0.9 * SEU_CANVAS_GRID_CELL_SIZE;
        } else {
          firstArrowPosition += 0.1 * SEU_CANVAS_GRID_CELL_SIZE;
        }
      } else {
        firstArrowPosition += 0.1 * SEU_CANVAS_GRID_CELL_SIZE;
      }

      arrows.push({
        dir: dyMain <= 0 ? 'up' : 'down',
        x: cummulatedPath.x,
        y:
          firstArrowPosition +
          i * (dyMain <= 0 ? -1 : 1) * SEU_CANVAS_GRID_CELL_SIZE,
      });
    }
  }

  cummulatedPath.y += dyMain;

  // Main horizontal segment
  let dxMain = deltaXPixel - SEU_CANVAS_GRID_CELL_SIZE;

  if (deltaY === 0) {
    if (deltaX < 0) {
      dxMain += 2 * SEU_BORDER_STREAM_OFFSET;
    } else {
      dxMain -= 2 * SEU_BORDER_STREAM_OFFSET;
    }
  }

  if (deltaX === 1) {
    // ** EDGE CASE ** 3-SEGMENT STREAM
    dxMain = 0;
  }

  if (dxMain !== 0) {
    steps.push({
      dx: dxMain,
      dy: 0,
    });

    // Add one arrow for every 288 pixels of deltaXPixel
    const numberOfArrowsDx =
      Math.floor(Math.abs(deltaXPixel) / SEU_CANVAS_GRID_CELL_SIZE) +
      (dxMain < 0 ? 1 : -1);
    for (let i = 0; i < numberOfArrowsDx; i++) {
      const arrowDistance =
        (i + 1) * SEU_CANVAS_GRID_CELL_SIZE - SEU_CANVAS_GRID_CELL_SIZE / 2;
      arrows.push({
        dir: dxMain < 0 ? 'left' : 'right',
        x: cummulatedPath.x + (dxMain < 0 ? -1 : 1) * arrowDistance,
        y: cummulatedPath.y,
      });
    }
  }

  cummulatedPath.x += dxMain;

  // Final vertical segment
  let dyFinal = endYPixel - cummulatedPath.y;

  if (deltaX === 1) {
    // ** EDGE CASE ** 3-SEGMENT STREAM
    // dyMain already moved us exactly to endYPixel; no final vertical correction needed.
    dyFinal = 0;
  }

  if (dyFinal !== 0) {
    steps.push({
      dx: 0,
      dy: dyFinal,
    });

    arrows.push({
      dir: dyFinal < 0 ? 'up' : 'down',
      x: cummulatedPath.x,
      y:
        cummulatedPath.y +
        (dyFinal < 0 ? -1 : 1) *
          (SEU_CANVAS_GRID_CELL_SIZE * 0.1 + SEU_BORDER_STREAM_OFFSET),
    });
  }

  // Ending segment
  let dxEnd = streamHandleOffsetEndX;

  if (deltaY > 0) {
    dxEnd -= 2 * SEU_BORDER_STREAM_OFFSET;
  }

  steps.push({
    dx: dxEnd,
    dy: 0,
  });

  return { steps, arrows };
}

export function buildSvgPath(
  startX: number,
  startY: number,
  steps: SeuStreamPathStep[]
): string {
  let d = `M ${startX} ${startY}`;
  for (const step of steps) {
    d += ` l ${step.dx} ${step.dy}`;
  }
  return d;
}

export function buildSvgArrow(arrow: SeuStreamPathArrow): string {
  switch (arrow.dir) {
    case 'up':
      return `M ${arrow.x} ${arrow.y} l -6 10 l 12 0 Z`;
    case 'down':
      return `M ${arrow.x} ${arrow.y} l -6 -10 l 12 0 Z`;
    case 'left':
      return `M ${arrow.x} ${arrow.y} l 10 -6 l 0 12 Z`;
    case 'right':
      return `M ${arrow.x} ${arrow.y} l -10 -6 l 0 12 Z`;
  }
}
